import {AppApiService} from "./app-api.service";
import type {ArticleListResponse} from "../models/interfaces/article-list-response.interface";
import type {ArticleDTO} from "../models/interfaces/article-dto.interface";
import type {CreateArticleRequest} from "../models/interfaces/create-article-request.interface";
import {UpdateArticleRequest} from "../models/interfaces/update-article-request.interface";
import {ArticleDetails} from "../models/interfaces/article-details.interface";
import {ArticleListApiParams} from "../models/interfaces/article-list-request-params.interface";
import {FileUploadResponse} from "../models/interfaces/file-upload-response.interface";
import {AiGeneratedArticlePayload} from "../../articles/models/interfaces/ai-generated-article.interface";

class ArticleApiService extends AppApiService {
    // Pobieranie listy artykułów
    public async getSearchArticles(params: ArticleListApiParams): Promise<ArticleListResponse> {
        return await this.get<ArticleListResponse>(`/article/search`, {
            params,
            errorMessage: "Błąd podczas pobierania artykułów",
        }).catch((error) => {
            throw error;
        });
    }

    // Pobieranie pojedynczego artykułu
    public async getArticleById(id: string): Promise<ArticleDetails> {
        return await this.get<ArticleDetails>(`/article/${id}`, {
            errorMessage: "Błąd podczas pobierania artykułu",
        }).catch((error) => {
            throw error;
        });
    }

    // Tworzenie nowego artykułu
    public async createArticle(data: CreateArticleRequest): Promise<ArticleDTO> {
        return await this.post<ArticleDTO>(`/article`, data, {
            errorMessage: "Błąd podczas tworzenia artykułu",
            successMessage: "Artykuł został utworzony pomyślnie",
        }).catch((error) => {
            throw error;
        });
    }

    // Aktualizacja artykułu
    public async updateArticle(data: UpdateArticleRequest): Promise<ArticleDetails> {
        return await this.put<ArticleDetails>(`/article`, data, {
            errorMessage: "Błąd podczas aktualizacji artykułu",
            // successMessage: "Artykuł został pomyślnie nadpisany"
        }).catch((error) => {
            throw error;
        });
    }

    // Usuwanie artykułu
    public async deleteArticle(id: string): Promise<void> {
        return await this.delete<void>(`/article/${id}`, {
            errorMessage: "Błąd podczas usuwania artykułu",
            successMessage: "Artykuł został usunięty pomyślnie",
        }).catch((error) => {
            throw error;
        });
    }

    // Publikowanie artykułu
    public async publishArticle(body: UpdateArticleRequest): Promise<void> {
        return await this.put<void>(`/article/publish`, body, {
            errorMessage: "Błąd podczas publikowania artykułu",
            successMessage: "Artykuł został opublikowany pomyślnie",
        }).catch((error) => {
            throw error;
        });
    }

    public async resetVersionsToPublished(id: string): Promise<void> {
        return await this.put<void>(`/article/reset/${id}`, {}, {
            errorMessage: "Nie udało się przywrócić stanu artykułu do ostatniej opublikowanej wersji",
            successMessage: "Stan artykułu przywrócono do ostatniej opublikowanej wersji"
        })
            .catch((error) => {
                throw error;
            });
    }

    public async uploadTempFile(file: File): Promise<FileUploadResponse> {
        const formData = new FormData();
        formData.append("multipartFile", file);

        return await this.post<FileUploadResponse>(`/article/file/upload`, formData, {
            errorMessage: "Błąd podczas uploadu pliku",
            successMessage: "Plik został przesłany pomyślnie"
        }).catch((error) => {
            throw error;
        });
    }

    // Pobieranie uniwersalnego artykułu
    public async getUniversalArticle(): Promise<ArticleDetails | null> {
        return await this.get<ArticleDetails | null>(`/article/universal`, {
            errorMessage: "Błąd podczas pobierania uniwersalnego artykułu",
        }).catch((error) => {
            throw error;
        });
    }

    public async getPostoperativeUniversalArticle(): Promise<ArticleDetails | null> {
        return await this.get<ArticleDetails | null>(`/article/postoperative`, {
            errorMessage: "Błąd podczas pobierania uniwersalnego pozabiegowego artykułu"
        })
            .catch((error) => {
                throw error;
            });
    }

    // Publiczne pobieranie artykułu (bez autoryzacji)
    public async getPublicArticle(name: string, appId: string,): Promise<ArticleDetails> {
        return await this.get<ArticleDetails>(`/article/public`, {
            params: {name, appId},
            errorMessage: "Błąd podczas pobierania artykułu"
        }).catch((error) => {
            throw error;
        });
    }

    // Publiczne pobieranie artykułu (bez autoryzacji)
    public async shareArticle(id: string, phoneNumber: string): Promise<ArticleDetails> {
        return await this.post<ArticleDetails>(`/article/share/sms`, {}, {
            params: {id, phoneNumber},
            errorMessage: "Błąd podczas udostępniania artykułu przez SMS"
        }).catch((error) => {
            throw error;
        });
    }

    // Ustawienie artykułu jako uniwersalnego
    public async setUniversalArticle(id: string): Promise<void> {
        return await this.patch<void>(`/article/universal`, {}, {
            errorMessage: "Błąd podczas ustawiania uniwersalnego artykułu",
            successMessage: "Uniwersalny artykuł został zaktualizowany",
            params: {id},
        }).catch((error) => {
            throw error;
        });
    }

    public async setPostOperativeUniversalArticle(id: string): Promise<void> {
        return await this.patch<void>(`/article/postoperative`, {}, {
            errorMessage: "Błąd podczas ustawiania uniwersalnego pozabiegowego artykułu",
            successMessage: "Uniwersalny pozabiegowy artykuł został zaktualizowany",
            params: {id},
        }).catch((error) => {
            throw error;
        });
    }

    public async generateArticleFromDocuments(files: File[]): Promise<AiGeneratedArticlePayload> {
        const formData = new FormData();
        files.forEach(file => formData.append("files", file));

        return await this.post<AiGeneratedArticlePayload>(`/article/ai/generate`, formData, {
            errorMessage: "Nie udało się wygenerować artykułu na podstawie dokumentów",
            successMessage: "Artykuł został przygotowany przez AI",
        }).catch((error) => {
            throw error;
        });
    }

    public async improveArticleWithAi(id: string): Promise<AiGeneratedArticlePayload> {
        return await this.post<AiGeneratedArticlePayload>(`/article/ai/improve/${id}`, {}, {
            errorMessage: "Nie udało się ulepszyć artykułu przy pomocy AI",
            successMessage: "AI przygotowało nową wersję artykułu",
        }).catch((error) => {
            throw error;
        });
    }
}

export default new ArticleApiService();
